<template>
  <v-card class="pa-6" outlined>
    <h3 class="black--text mb-1">{{ $t("reports.aiCredit.ratesTitle") }}</h3>
    <p class="grey--text mb-5" style="max-width: 1020px">
      {{ $t("reports.aiCredit.ratesHelp") }}
    </p>

    <v-progress-linear
      v-if="loading"
      indeterminate
      color="primary"
      class="mb-4"
    />

    <!-- Per report-type rate rows -->
    <div v-for="row in rows" :key="row.reportType" class="mb-5">
      <div class="black--text mb-2">{{ reportLabel(row.reportType) }}</div>
      <div class="rate-control">
        <input
          v-model.number="row.creditCost"
          type="number"
          min="0"
          step="0.5"
          class="rate-input"
          @input="markDirty"
        />
        <span class="rate-suffix">{{ $t("reports.aiCredit.credits") }}</span>
        <v-menu offset-y>
          <template v-slot:activator="{ on, attrs }">
            <button type="button" class="rate-calc" v-bind="attrs" v-on="on">
              {{ calcLabel(row.calcType) }}
              <v-icon small dark class="ml-1">mdi-chevron-down</v-icon>
            </button>
          </template>
          <v-list dense>
            <v-list-item @click="setCalc(row, 'per_acre')">
              <v-list-item-title>{{
                $t("reports.aiCredit.perAcre")
              }}</v-list-item-title>
            </v-list-item>
            <v-list-item @click="setCalc(row, 'per_report')">
              <v-list-item-title>{{
                $t("reports.aiCredit.perReport")
              }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </div>
    </div>

    <v-divider class="my-5" />

    <!-- Rollover settings -->
    <h3 class="black--text mb-1">{{ $t("reports.aiCredit.rolloverTitle") }}</h3>
    <p class="grey--text mb-3">{{ $t("reports.aiCredit.rolloverHelp") }}</p>

    <div class="d-flex align-center">
      <span class="black--text font-weight-medium mr-4">{{
        $t("reports.aiCredit.rolloverUnused")
      }}</span>
      <span
        class="mr-2"
        :class="rollover ? 'green--text text--darken-1' : 'grey--text'"
      >
        {{ rollover ? $t("reports.aiCredit.yes") : $t("reports.aiCredit.no") }}
      </span>
      <v-switch
        v-model="rollover"
        class="mt-0"
        hide-details
        dense
        @change="markDirty"
      />
    </div>

    <v-divider class="my-5" />

    <!-- Actions -->
    <div class="d-flex justify-end" style="gap: 10px">
      <v-btn outlined color="primary" :disabled="loading" @click="cancel">{{
        $t("reports.aiCredit.cancel")
      }}</v-btn>
      <v-btn
        color="primary"
        depressed
        :loading="saving"
        :disabled="loading"
        @click="save"
      >
        {{ $t("reports.aiCredit.save") }}
      </v-btn>
    </div>
  </v-card>
</template>

<script>
import AiCreditService from "@/_services/AiCreditService";

// Fixed product order for the rate rows (backend returns them alphabetically).
const ORDER = [
  "vegetation_vigor",
  "farm_productivity",
  "anomaly_detection",
  "yield_prediction",
  "disease_detection",
  "land_suitability",
];

export default {
  name: "CreditRates",
  data() {
    return {
      rows: [],
      rollover: false,
      loading: false,
      saving: false,
      dirty: false,
    };
  },
  created() {
    this.fetch();
  },
  methods: {
    reportLabel(reportType) {
      const key = `reports.aiCredit.rt_${reportType}`;
      const label = this.$t(key);
      return label === key ? reportType : label;
    },
    calcLabel(calcType) {
      return calcType === "per_acre"
        ? this.$t("reports.aiCredit.perAcre")
        : this.$t("reports.aiCredit.perReport");
    },
    async fetch() {
      this.loading = true;
      try {
        const env = await AiCreditService.listRates();
        const rates = (env && env.data && env.data.rates) || [];
        const byType = {};
        rates.forEach((r) => (byType[r.reportType] = r));
        // Keep any returned types not in ORDER at the end.
        const ordered = [
          ...ORDER,
          ...rates.map((r) => r.reportType).filter((t) => !ORDER.includes(t)),
        ];
        this.rows = ordered
          .filter((t) => byType[t])
          .map((t) => {
            const r = byType[t];
            return {
              reportType: r.reportType,
              creditCost: Number(r.creditCost),
              calcType: r.calcType,
              active: r.active !== false, // preserved on save (not shown in this screen)
            };
          });
        // Single global rollover toggle = rollover enabled across all rates.
        this.rollover =
          this.rows.length > 0 && rates.every((r) => !!r.rolloverEnabled);
        this.dirty = false;
      } catch (e) {
        this.$notify({
          title: this.$t("reports.aiCredit.errorTitle"),
          text: this.$t("reports.aiCredit.loadFailed"),
          type: "error",
        });
      } finally {
        this.loading = false;
      }
    },
    markDirty() {
      this.dirty = true;
    },
    setCalc(row, calcType) {
      row.calcType = calcType;
      this.markDirty();
    },
    cancel() {
      this.fetch();
    },
    async save() {
      this.saving = true;
      try {
        const rates = this.rows.map((row) => ({
          reportType: row.reportType,
          creditCost: Number(row.creditCost) || 0,
          calcType: row.calcType,
          rolloverEnabled: this.rollover, // global toggle applies to every rate
          active: row.active,
        }));
        await AiCreditService.bulkUpsertRates(rates);
        this.dirty = false;
        this.$notify({
          title: this.$t("reports.aiCredit.successTitle"),
          text: this.$t("reports.aiCredit.saved"),
          type: "success",
        });
      } catch (e) {
        this.$notify({
          title: this.$t("reports.aiCredit.errorTitle"),
          text: this.$t("reports.aiCredit.saveFailed"),
          type: "error",
        });
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.rate-control {
  display: inline-flex;
  align-items: stretch;
  border: 1px solid #d9d9d9;
  border-radius: 8px;
  overflow: hidden;
  height: 48px;
  max-width: 360px;
  width: 100%;
}
.rate-input {
  flex: 1 1 auto;
  min-width: 0;
  border: none;
  outline: none;
  padding: 0 14px;
  font-size: 15px;
  color: #222;
}
/* hide number spinners */
.rate-input::-webkit-outer-spin-button,
.rate-input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
.rate-input {
  -moz-appearance: textfield;
}
.rate-suffix {
  display: flex;
  align-items: center;
  padding: 0 12px;
  color: #9e9e9e;
  font-size: 14px;
}
.rate-calc {
  display: flex;
  align-items: center;
  padding: 0 16px;
  background: #21b573;
  color: #fff;
  font-weight: 600;
  font-size: 14px;
  border: none;
  cursor: pointer;
  white-space: nowrap;
}
.rate-calc:hover {
  background: #1ca768;
}
</style>
